import clsx from 'clsx';
import {
  ListingMainMetrics,
  ListingSubMetrics,
  SaleHeaderMetrics,
  SaleMainMetrics,
  SaleSubMetrics,
} from 'src/components/Metrics';
import {
  ListingMainMetricsV2,
  ListingSubMetricsV2,
} from 'src/components/Metrics/ListingMetricsV2';
import {
  BaseContentProps,
  Content,
  FormatContent,
} from 'src/contexts/ContentContext';
import { ContentPlaceholder } from 'src/core/POS/ContentPlaceholder';
import { vars } from 'src/core/themes';
import { MarginMetricIcon, SalesMetricIcon, SignalIcon } from 'src/svgs';
import { UnlistedTicketsIcon } from 'src/svgs/UnlistedTicketsIcon';
import {
  ListingMetrics,
  SaleMetrics,
  Tag,
  TicketGroupMetrics,
  UiMoney,
} from 'src/WebApiController';

import { ContentId } from './constants/contentId';
import { FormatContentId } from './constants/formatContentId';
import { getCurrencyNumberFormat } from './format';
import { getCurrentLanguage } from './localeUtils';
import * as styles from './ticketMetricUtils.css';

// TODO:
// - Unit test file
// - Are the other untranslated parts even used/needed?
export const getMetricsDisplay = (
  key: keyof SaleMetrics,
  params: BaseContentProps['params']
) => {
  switch (key) {
    case 'qtySold':
      return <Content id={ContentId.Sold} />;
    case 'saleUnfilled':
      return <FormatContent id={FormatContentId.Filled} params={params} />;
    case 'netProcs':
      return <Content id={ContentId.Total} />;
    case 'marg':
      return <FormatContent id={FormatContentId.Margin} params={params} />;
    case 'ttlCst':
      return <Content id={ContentId.CostBasis} />;
    default:
      return '';
  }
};

export type InventorySummaryListingMetricsDisplayStrings = {
  formattedTotalSoldQuantity: string;
  formattedTotalTicketQuantity: string;
  formattedTotalSoldPercentage: string;
  formattedTotalListPrice: string;
  formattedTotalNetProceeds: string;
  formattedTotalGrossMargin: string;
  formattedAverageSalePrice: string;
  formattedAverageListPrice: string;
  totalSoldPercentage: number;
  totalGrossMargin: number;
};

export const getInventorySummaryListingMetricsDisplayStrings = (
  {
    soldQty: totalSoldQuantity,
    tktQty: totalTicketQuantity,
    currency: currencyCode,
    ttlListPrc: totalListPrice,
    netProcs: totalNetProceeds,
    pandL: totalGrossProfit,
    soldCst: totalSoldCost,
    unsoldCst: totalUnsoldCost,
    unsoldQty: totalUnsoldTicket,
    ttlUnsoldListPrc: totalUnsoldListPrice,
  }: ListingMetrics,
  currency?: string | null
) => {
  const quantityNumberFormat = new Intl.NumberFormat(getCurrentLanguage(), {
    style: 'decimal',
    notation: 'compact',
  });

  const curCode = currencyCode || currency || 'USD';
  const currencyNumberFormat = new Intl.NumberFormat(getCurrentLanguage(), {
    style: 'currency',
    notation: 'compact',
    currency: curCode,
  });
  const rawCurrencyNumberFormat = new Intl.NumberFormat(getCurrentLanguage(), {
    style: 'currency',
    currency: curCode,
  });

  const totalGrossMargin =
    totalGrossProfit > 0 && totalNetProceeds > 0
      ? totalGrossProfit / totalNetProceeds
      : 0;
  const totalSoldPercentage =
    totalTicketQuantity > 0 ? totalSoldQuantity / totalTicketQuantity : 0;
  const averageSalePrice =
    totalNetProceeds > 0 && totalSoldQuantity > 0
      ? totalNetProceeds / totalSoldQuantity
      : 0;
  return {
    formattedTotalSoldQuantity: quantityNumberFormat.format(
      totalSoldQuantity ?? 0
    ),
    formattedTotalTicketQuantity:
      quantityNumberFormat.format(totalTicketQuantity),
    formattedTotalUnsoldTicketQuantity:
      quantityNumberFormat.format(totalUnsoldTicket),
    formattedTotalUnsoldCost: rawCurrencyNumberFormat.format(totalUnsoldCost),
    formattedTotalUnsoldCostAb: getCurrencyNumberFormat(
      totalUnsoldCost,
      curCode
    ).format(totalUnsoldCost),
    formattedTotalSoldPercentage: (totalSoldPercentage ?? 0).toLocaleString(
      undefined,
      { style: 'percent', minimumFractionDigits: 0 }
    ),
    formattedTotalListPrice: new Intl.NumberFormat(getCurrentLanguage(), {
      style: 'currency',
      currency: curCode,
    }).format(totalListPrice ?? 0),
    formattedTotalNetProceeds: currencyNumberFormat.format(
      totalNetProceeds ?? 0
    ),
    formattedTotalGrossMargin: (totalGrossMargin ?? 0).toLocaleString(
      undefined,
      {
        style: 'percent',
        minimumFractionDigits: 0,
      }
    ),
    formattedAverageSalePrice: currencyNumberFormat.format(
      averageSalePrice ?? 0
    ),
    formattedAverageListPrice: currencyNumberFormat.format(
      totalUnsoldTicket > 0 && totalUnsoldListPrice > 0
        ? totalUnsoldListPrice / totalUnsoldTicket
        : 0
    ),
    formattedProfitAndLoss: rawCurrencyNumberFormat.format(totalGrossProfit),
    formattedProfitAndLossAb: getCurrencyNumberFormat(
      totalGrossProfit,
      curCode
    ).format(totalGrossProfit),
    totalSoldPercentage,
    totalGrossMargin,
  };
};

export const getInventoryListingMetricsDisplayStrings = (
  {
    tktQty: totalTicketQuantity,
    unsoldQty: totalUnsoldQuantity,
    listQty: totalListedQuantity,
    unlistQty: totalUnlistedQuantity,
    currency: currencyCode,
    ttlCst: totalCost,
    unsoldCst: totalUnsoldCost,
    ttlUnsoldListPrc: totalUnsoldListPrice,
    netProcs: totalNetProceeds,
    pandL: totalGrossProfit,
  }: ListingMetrics,
  currency?: string | null
) => {
  const BIG_NUMBER_THRESHOLD = 1000;
  const curCode = currencyCode || currency || 'USD';

  const getFractionDigits = (val: number) =>
    val >= BIG_NUMBER_THRESHOLD ? 1 : 0;

  const getQuantityNumberFormat = () =>
    Intl.NumberFormat(undefined, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const getCurrencyNumberFormat = (val: number) =>
    Intl.NumberFormat(undefined, {
      style: 'currency',
      notation: 'compact',
      currency: curCode,
      minimumFractionDigits: getFractionDigits(val),
      maximumFractionDigits: getFractionDigits(val),
    });

  const totalCostAmount = totalCost ?? 0;
  const totalUnsoldCostAmount = totalUnsoldCost ?? 0;
  const totalUnsoldListPriceAmount = totalUnsoldListPrice ?? 0;
  const totalNetProceedsAmount = totalNetProceeds ?? 0;
  const totalGrossProfitAmount = totalGrossProfit ?? 0;
  const totalGrossMargin =
    totalNetProceeds > 0 ? (totalGrossProfit ?? 0) / totalNetProceeds : 0;

  return {
    formattedTotalTicketQuantity: getQuantityNumberFormat().format(
      totalTicketQuantity ?? 0
    ),
    formattedTotalUnsoldQuantity: getQuantityNumberFormat().format(
      totalUnsoldQuantity ?? 0
    ),
    formattedTotalListedQuantity: getQuantityNumberFormat().format(
      totalListedQuantity ?? 0
    ),
    formattedTotalUnlistedQuantity: getQuantityNumberFormat().format(
      totalUnlistedQuantity ?? 0
    ),
    formattedTotalCost:
      getCurrencyNumberFormat(totalCostAmount).format(totalCostAmount),
    formattedTotalUnsoldCost: getCurrencyNumberFormat(
      totalUnsoldCostAmount
    ).format(totalUnsoldCostAmount),
    formattedTotalUnsoldListPrice: getCurrencyNumberFormat(
      totalUnsoldListPriceAmount
    ).format(totalUnsoldListPriceAmount),
    formattedTotalNetProceeds: getCurrencyNumberFormat(
      totalNetProceedsAmount
    ).format(totalNetProceedsAmount),

    formattedTotalGrossMargin: totalGrossMargin.toLocaleString(undefined, {
      style: 'percent',
      minimumFractionDigits: 0,
    }),
    formattedTotalGrossProfit: getCurrencyNumberFormat(
      totalGrossProfitAmount
    ).format(totalGrossProfitAmount),
  };
};

type InventoryListingMetricsDisplay = {
  mainMetrics: JSX.Element;
  subMetrics: JSX.Element;
  tags?: Tag[] | null;
};

/**
 * Displaying metrics for the inventory listing (v2)
 * Main Metric: UnsoldTickets / TotalTickets
 * Sub Metrics: UnsoldCost, ListedQuantity, UnlistedQuantity
 */
export const getInventoryListingMetricsV2 = (
  metrics: ListingMetrics | null | undefined,
  currencyCode?: string | null
): InventoryListingMetricsDisplay => {
  if (!metrics) {
    return {
      mainMetrics: <ContentPlaceholder height="30px" width="64px" />,
      subMetrics: (
        <ListingSubMetricsV2
          totalPandL={
            <>
              <ContentPlaceholder height="16px" width="32px" />
            </>
          }
          totalUnsoldCost={
            <>
              <ContentPlaceholder height="16px" width="32px" />
            </>
          }
          totalUnlistedQuantity={
            <>
              <UnlistedTicketsIcon size={vars.iconSize.m} />
              <ContentPlaceholder height="16px" width="32px" />
            </>
          }
        />
      ),
    };
  }
  const {
    formattedTotalTicketQuantity,
    formattedTotalUnsoldQuantity,
    formattedTotalUnsoldCost: displayTotalUnsoldCost,
    formattedTotalGrossProfit,
    formattedTotalUnlistedQuantity,
  } = getInventoryListingMetricsDisplayStrings(metrics, currencyCode);

  const isLoss = (metrics.pandL ?? 0) < 0;
  const isProfit = (metrics.pandL ?? 0) > 0;
  const displayTotalGrossProfit = `${
    isProfit ? '+' : ''
  }${formattedTotalGrossProfit}`;

  return {
    mainMetrics: (
      <ListingMainMetricsV2
        unsoldTicketsCount={formattedTotalUnsoldQuantity}
        ticketsCount={formattedTotalTicketQuantity}
      />
    ),
    subMetrics: (
      <ListingSubMetricsV2
        totalPandL={
          <>
            <span
              className={clsx({
                [styles.profitText]: isProfit,
                [styles.lossText]: isLoss,
              })}
            >
              {displayTotalGrossProfit}
            </span>
          </>
        }
        totalUnsoldCost={
          <>
            <span>{displayTotalUnsoldCost}</span>
          </>
        }
        totalUnlistedQuantity={
          <>
            <span className={styles.iconContainer}>
              <UnlistedTicketsIcon size={vars.iconSize.xs} />
            </span>
            <span>{formattedTotalUnlistedQuantity}</span>
          </>
        }
      />
    ),
    tags: metrics.tags,
  };
};

/**
 * Try to add money from a list of items, if the currencies are the same.
 * If the currencies are not the same, return null.
 * @param list - List of items to add
 * @param getAmount - Function to get the amount from an item
 * @param getCurrency - Function to get the currency from an item
 * @returns The sum of the amounts, or null if the list is empty or the currencies are not the same
 */
export function tryAddMoneySameCurrency<T>(
  list: T[],
  getAmount: (item: T) => number | null | undefined,
  getCurrency: (item: T) => string | null | undefined
): null | UiMoney {
  if (list.length === 0) {
    return null;
  }

  const listWithNonZeroAmounts = list.filter((item) => !!getAmount(item));

  const firstItemWithCurrency =
    listWithNonZeroAmounts.find((item) => getCurrency(item) != null) ??
    list.find((item) => getCurrency(item) != null);

  if (!firstItemWithCurrency) {
    return null;
  }

  const currency = getCurrency(firstItemWithCurrency)!;

  const allHaveSameOrNullCurrency = listWithNonZeroAmounts.every(
    (item) => getCurrency(item) === currency || getCurrency(item) == null
  );

  if (!allHaveSameOrNullCurrency) {
    return null;
  }

  const total = listWithNonZeroAmounts.reduce((acc, item) => {
    const amount = getAmount(item);
    if (amount == null) {
      return acc;
    }
    return acc + amount;
  }, 0);

  return {
    amt: total,
    currency,
  } as UiMoney;
}

/**
 * Add two numbers, if both are not null.
 * If one is null, return the other.
 * This doesn't check if the currencies are the same.
 * @param a - First number
 * @param b - Second number
 * @returns The sum of the numbers, or null if both are null
 */
export const addMoneySameCurrency = (
  a: number | null | undefined,
  b: number | null | undefined
): number | null => {
  if (!a && !b) {
    return null;
  }
  if (a && !b) {
    return a;
  }
  if (!a && b) {
    return b;
  }

  const amt = a! + b!;
  return amt;
};

// add tags and deduplicate by key/value
const mergeAndDeduplicateTags = (
  tagsA: Tag[] | null | undefined,
  tagsB: Tag[] | null | undefined
): Tag[] | null => {
  const allTags = (tagsA ?? []).concat(tagsB ?? []);

  if (allTags.length === 0) {
    return null;
  }

  const seen = new Set<string>();
  return allTags.filter((tag) => {
    const id = `${tag.key}|${tag.value}`;
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
};

export const addListingMetrics = (
  metricA: ListingMetrics,
  metricB: ListingMetrics
): ListingMetrics => {
  const currencyCode = metricA.currency;

  const addListingMoney = (
    a: number | null | undefined,
    b: number | null | undefined
  ) => addMoneySameCurrency(a, b);

  return {
    tktQty: metricA.tktQty + metricB.tktQty,
    soldQty: metricA.soldQty + metricB.soldQty,
    unsoldQty: metricA.unsoldQty + metricB.unsoldQty,
    listQty: metricA.listQty + metricB.listQty,
    unlistQty: metricA.listQty + metricB.unlistQty,
    currency: currencyCode,

    ttlCst: addListingMoney(metricA.ttlCst, metricB.ttlCst)!,
    soldCst: addListingMoney(metricA.soldCst, metricB.soldCst)!,
    unsoldCst: addListingMoney(metricA.unsoldCst, metricB.unsoldCst)!,
    ttlListPrc: addListingMoney(metricA.ttlListPrc, metricB.ttlListPrc)!,
    ttlUnsoldListPrc: addListingMoney(
      metricA.ttlUnsoldListPrc,
      metricB.ttlUnsoldListPrc
    )!,
    netProcs: addListingMoney(metricA.netProcs, metricB.netProcs)!,
    pandL: addListingMoney(metricA.pandL, metricB.pandL)!,
    tags: mergeAndDeduplicateTags(metricA.tags, metricB.tags),
  };
};

export const addSaleMetrics = (
  metricA: SaleMetrics,
  metricB: SaleMetrics
): SaleMetrics => {
  const currencyCode = metricA.currency ?? metricB.currency;

  return {
    qtySold: metricA.qtySold + metricB.qtySold,
    saleUnfilled: metricA.saleUnfilled + metricB.saleUnfilled,
    netProcs: addMoneySameCurrency(metricA.netProcs, metricB.netProcs),
    ttlCst: addMoneySameCurrency(metricA.ttlCst, metricB.ttlCst),
    currency: currencyCode,
    marg:
      metricA.marg == null && metricB.marg == null
        ? null
        : (metricA.marg ?? 0) + (metricB.marg ?? 0),
    tags: mergeAndDeduplicateTags(metricA.tags, metricB.tags),
  };
};

export const addTicketGroupMetrics = (
  metricA: TicketGroupMetrics,
  metricB: TicketGroupMetrics
): TicketGroupMetrics => {
  const currencyCode = metricA.currency ?? metricB.currency;

  return {
    tktQty: metricA.tktQty + metricB.tktQty,
    ttlCst: addMoneySameCurrency(metricA.ttlCst, metricB.ttlCst),
    currency: currencyCode,
    tags: mergeAndDeduplicateTags(metricA.tags, metricB.tags),
  };
};

export const getInventoryListingMetrics = (
  metrics: ListingMetrics | null | undefined,
  currencyCode?: string | null
): InventoryListingMetricsDisplay => {
  if (!metrics) {
    return {
      mainMetrics: <ContentPlaceholder height="30px" width="64px" />,
      subMetrics: (
        <ListingSubMetrics
          totalUnsoldListPrice={
            <>
              <SignalIcon size={vars.iconSize.xs} />
              <ContentPlaceholder height="16px" width="32px" />
            </>
          }
          totalNetProceeds={
            <>
              <SalesMetricIcon size={vars.iconSize.xs} />
              <ContentPlaceholder height="16px" width="32px" />
            </>
          }
          totalGrossMargin={
            <>
              <MarginMetricIcon size={vars.iconSize.xs} />
              <ContentPlaceholder height="16px" width="32px" />
            </>
          }
        />
      ),
    };
  }
  const {
    formattedTotalTicketQuantity,
    formattedTotalUnsoldListPrice,
    formattedTotalNetProceeds,
    formattedTotalGrossMargin,
  } = getInventoryListingMetricsDisplayStrings(metrics, currencyCode);
  return {
    mainMetrics: (
      <ListingMainMetrics ticketsCount={formattedTotalTicketQuantity} />
    ),
    subMetrics: (
      <ListingSubMetrics
        totalUnsoldListPrice={
          <>
            <span className={styles.iconContainer}>
              <SignalIcon size={vars.iconSize.xs} />
            </span>
            {formattedTotalUnsoldListPrice}
          </>
        }
        totalNetProceeds={
          <>
            <span className={styles.iconContainer}>
              <SalesMetricIcon size={vars.iconSize.xs} />
            </span>
            {formattedTotalNetProceeds}
          </>
        }
        totalGrossMargin={
          <>
            <span className={styles.iconContainer}>
              <MarginMetricIcon size={vars.iconSize.xs} />
            </span>
            {formattedTotalGrossMargin}
          </>
        }
      />
    ),
  };
};

export const getSalesSaleMetrics = (
  metrics: SaleMetrics | null | undefined,
  addBulletPoint = true,
  firstBulletPoint = true,
  secondBulletPoint = true
) => {
  const currencyNumberFormat = new Intl.NumberFormat(getCurrentLanguage(), {
    style: 'currency',
    notation: 'compact',
    currency: metrics?.currency || 'USD',
  });

  return metrics === undefined
    ? null
    : !metrics
      ? { headerMetrics: [], mainMetrics: '', subMetrics: [], tags: null }
      : {
          headerMetrics: (
            <SaleHeaderMetrics needFulfillment={metrics.saleUnfilled > 0} />
          ),
          mainMetrics: (
            <SaleMainMetrics
              totalNetProceeds={
                metrics.netProcs == null
                  ? null
                  : currencyNumberFormat.format(metrics.netProcs)
              }
            />
          ),
          subMetrics: (
            <SaleSubMetrics
              margin={metrics.marg}
              ticketsFulfilled={metrics.qtySold - metrics.saleUnfilled}
              ticketsSold={metrics.qtySold}
              addBulletPoint={addBulletPoint}
              firstBulletPoint={firstBulletPoint}
              secondBulletPoint={secondBulletPoint}
            />
          ),
          tags: metrics.tags,
        };
};

export const getEmptyMetric = () =>
  ({
    ticketsTotal: 0,
    qtySold: 0,
    saleUnfilled: 0,
    netProcs: 0,
    ttlCst: 0,
    marg: 0,
    currency: null,
    tags: null,
  }) as SaleMetrics;
